/**
 * <ry-combobox>
 *
 * Searchable dropdown with text input filtering.
 *
 * Usage:
 * <ry-combobox placeholder="Search countries..." name="country">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 * </ry-combobox>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';
import type { SelectOption, ChangeDetail } from '../types.js';

let comboboxIdCounter = 0;

export class RyCombobox extends RyElement {
  #comboboxId: string | null = null;
  #highlightedIndex = -1;
  #options: SelectOption[] = [];

  static observedAttributes = ['value', 'disabled'] as const;

  setup(): void {
    this.#comboboxId = `ry-combobox-${++comboboxIdCounter}`;
    this.#transformMarkup();

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');

    if (input) {
      this.on(input, 'input', this.#handleInputEvent);
      this.on(input, 'focus', this.#handleFocus);
    }

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
    this.on(this, 'mouseover', this.#handleMouseover);
    this.on(document, 'click', this.#handleOutsideClick);

    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }

    const initialValue = this.getAttribute('value');
    if (initialValue) {
      this.value = initialValue;
    }
  }

  #transformMarkup(): void {
    const options: SelectOption[] = [...this.querySelectorAll('ry-option')].map(opt => ({
      value: opt.getAttribute('value') ?? opt.textContent?.trim() ?? '',
      label: opt.textContent?.trim() ?? '',
      disabled: opt.hasAttribute('disabled')
    }));

    const placeholder = this.getAttribute('placeholder') ?? 'Search...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');

    const nativeOptions = options.map(opt =>
      `<option value="${opt.value}"${opt.disabled ? ' disabled' : ''}>${opt.label}</option>`
    ).join('');

    const listboxOptions = options.map((opt, i) => `
      <div data-ry-target="option" class="ry-combobox__option"
           role="option"
           id="${this.#comboboxId}-option-${i}"
           data-value="${opt.value}"
           ${opt.disabled ? 'data-disabled' : ''}
           aria-disabled="${opt.disabled}">
        ${opt.label}
      </div>
    `).join('');

    this.innerHTML = `
      <div data-ry-target="input-wrapper" class="ry-combobox__input-wrapper"
           role="combobox"
           aria-expanded="false"
           aria-haspopup="listbox"
           aria-owns="${this.#comboboxId}-listbox">
        <input data-ry-target="input" class="ry-combobox__input"
               type="text"
               placeholder="${placeholder}"
               autocomplete="off"
               aria-autocomplete="list"
               aria-controls="${this.#comboboxId}-listbox"
               ${disabled ? 'disabled' : ''}>
        <span data-ry-target="arrow" class="ry-combobox__arrow">&#9662;</span>
      </div>
      <div data-ry-target="dropdown" class="ry-combobox__dropdown"
           role="listbox"
           id="${this.#comboboxId}-listbox"
           tabindex="-1">
        ${listboxOptions}
        <div data-ry-target="empty" class="ry-combobox__empty" hidden>
          No results
        </div>
      </div>
      <select data-ry-target="native" class="ry-combobox__native"
              ${name ? `name="${name}"` : ''}
              tabindex="-1" aria-hidden="true"
              ${disabled ? 'disabled' : ''}>
        <option value="">${placeholder}</option>
        ${nativeOptions}
      </select>
    `;

    this.#options = options;
  }

  #handleInputEvent = (): void => {
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (!input) return;

    const query = input.value.trim();
    this.#filterOptions(query);

    if (this.state !== 'open') {
      this.open();
    }

    this.emit('input', { value: query });
  };

  #filterOptions(query: string): void {
    const options = this.$$<HTMLElement>('[data-ry-target="option"]');
    const lowerQuery = query.toLowerCase();
    let visibleCount = 0;

    options.forEach(opt => {
      const label = opt.textContent?.trim().toLowerCase() ?? '';
      const matches = !query || label.includes(lowerQuery);
      if (matches && !opt.hasAttribute('data-disabled')) {
        opt.removeAttribute('hidden');
        visibleCount++;
      } else {
        opt.setAttribute('hidden', '');
      }
    });

    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      if (visibleCount === 0) {
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    this.#highlightedIndex = -1;
    this.$$('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (option instanceof HTMLElement && !option.hasAttribute('data-disabled')) {
      this.#selectOption(option);
      return;
    }

    const arrow = target.closest('[data-ry-target="arrow"]');
    if (arrow) {
      this.toggle();
      return;
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (this.hasAttribute('disabled')) return;
    const isOpen = this.state === 'open';

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.open();
        } else {
          this.#highlightNext();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          this.#highlightPrev();
        }
        break;

      case 'Enter':
        if (isOpen && this.#highlightedIndex >= 0) {
          event.preventDefault();
          const visibleOptions = this.$$<HTMLElement>(
            '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
          );
          const option = visibleOptions[this.#highlightedIndex];
          if (option) {
            this.#selectOption(option);
          }
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.close();
        }
        break;

      case 'Tab':
        if (isOpen) {
          this.close();
        }
        break;
    }
  };

  #handleMouseover = (event: MouseEvent): void => {
    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (!(option instanceof HTMLElement) || option.hasAttribute('data-disabled') || option.hasAttribute('hidden')) return;

    const visibleOptions = this.$$<HTMLElement>(
      '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
    );
    const index = visibleOptions.indexOf(option);
    if (index >= 0) {
      this.#highlightIndex(index);
    }
  };

  #handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (!this.contains(target) && this.state === 'open') {
      this.close();
    }
  };

  #handleFocus = (): void => {
    if (this.hasAttribute('disabled')) return;

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input && input.value) {
      input.select();
    }

    this.open();
  };

  #selectOption(optionEl: HTMLElement): void {
    const value = optionEl.dataset.value ?? '';
    const label = optionEl.textContent?.trim() ?? '';

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) {
      input.value = label;
    }

    const nativeSelect = this.$<HTMLSelectElement>('[data-ry-target="native"]');
    if (nativeSelect) {
      nativeSelect.value = value;
    }

    this.setAttribute('value', value);

    this.$$('[data-ry-target="option"]').forEach(opt => {
      opt.removeAttribute('aria-selected');
    });
    optionEl.setAttribute('aria-selected', 'true');

    this.close();
    this.emit<ChangeDetail>('change', { value, label });
  }

  #highlightIndex(index: number): void {
    const options = this.$$<HTMLElement>(
      '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
    );
    if (index < 0 || index >= options.length) return;

    this.$$('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });

    const option = options[index];
    if (!option) return;

    option.setAttribute('data-highlighted', '');
    option.scrollIntoView({ block: 'nearest' });
    this.#highlightedIndex = index;

    const wrapper = this.$('[data-ry-target="input-wrapper"]');
    if (wrapper) {
      wrapper.setAttribute('aria-activedescendant', option.id);
    }
  }

  #highlightNext(): void {
    const options = this.$$('[data-ry-target="option"]:not([hidden]):not([data-disabled])');
    const next = Math.min(this.#highlightedIndex + 1, options.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  #positionDropdown(): void {
    const dropdown = this.$('[data-ry-target="dropdown"]');
    if (!dropdown) return;

    this.removeAttribute('data-ry-position');

    const rect = this.getBoundingClientRect();
    const dropdownHeight = (dropdown as HTMLElement).scrollHeight || 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      this.setAttribute('data-ry-position', 'top');
    }
  }

  open(): void {
    if (this.state === 'open') return;

    this.state = 'open';

    const wrapper = this.$('[data-ry-target="input-wrapper"]');
    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'true');
    }

    // Show all options on open (clear filter)
    this.$$<HTMLElement>('[data-ry-target="option"]').forEach(opt => {
      if (!opt.hasAttribute('data-disabled')) {
        opt.removeAttribute('hidden');
      }
    });
    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      emptyEl.setAttribute('hidden', '');
    }

    this.#positionDropdown();

    // Highlight current value if set
    const currentValue = this.getAttribute('value');
    if (currentValue) {
      const options = this.$$<HTMLElement>(
        '[data-ry-target="option"]:not([hidden]):not([data-disabled])'
      );
      const index = options.findIndex(opt => opt.dataset.value === currentValue);
      if (index >= 0) {
        this.#highlightIndex(index);
      }
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;

    this.state = 'closed';

    const wrapper = this.$('[data-ry-target="input-wrapper"]');
    if (wrapper) {
      wrapper.setAttribute('aria-expanded', 'false');
      wrapper.removeAttribute('aria-activedescendant');
    }

    this.$$('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
    this.#highlightedIndex = -1;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    const option = this.$<HTMLElement>(`[data-ry-target="option"][data-value="${val}"]`);
    if (option) {
      this.#selectOption(option);
    }
  }
}

customElements.define('ry-combobox', RyCombobox);
